/**
 * Netplay session start rules
 * (docs/superpowers/specs/2026-10-02-netplay-reset-replay-boundary-design.md §6).
 * Functions take the NESPlay instance as `app`; main.js keeps delegates.
 * This module never imports main.js.
 */
import { monoNow, CLEANUP_MS, STAGE_MS, recorderRetainedBytes, drainRecorder } from './replay-boundary.js';

export const START_MESSAGE = 'The game could not start. Leave the room and try again.';
export const SAVE_MESSAGE = 'This save could not be loaded, so the game did not start. Leave the room and try again.';
export const RELOAD_MESSAGE = 'Reload the page to play again.';

const SAVE_CAUSES = new Set(['netplay_start_invalid_frame', 'netplay_start_load_unconfirmed', 'netplay_start_save_unavailable']);
const RELOAD_CAUSES = new Set(['netplay_start_quarantined', 'netplay_start_cleanup_unconfirmed']);

/** §8 messages for a refused start. */
export function startMessageFor(cause) {
  if (RELOAD_CAUSES.has(cause)) return RELOAD_MESSAGE;
  return SAVE_CAUSES.has(cause) ? SAVE_MESSAGE : START_MESSAGE;
}

/** One shared retirement responsibility, with a separate late-init cleanup phase. */
export function coreRetirementTicket(emu, initializing = false) {
  return { emu, initializing, outcome: null, cleanupErrors: [], late: false,
    cleanupDeadline: null, waitingSessions: new Map(), evidenceTargets: new Set() };
}

function uniqueErrors(...arrays) { return [...new Set(arrays.flat())]; }

/** Retain completed and later native cleanup in every outcome that owns it. */
function refreshCoreEvidence(ticket) {
  for (const [session, earlier] of ticket.waitingSessions) {
    const stage = earlier.retirementResult;
    if (!stage) continue;
    const native = earlier.nativeRetirement;
    stage.cleanupErrors = uniqueErrors(native.cleanupErrors, ticket.cleanupErrors);
    stage.late = native.late || ticket.late;
    stage.confirmed = native.confirmed && ticket.outcome?.confirmed === true && !ticket.initializing;
    stage.status = stage.confirmed ? 'ok' : stage.late ? 'timeout' : 'failed';
    stage.cause = stage.confirmed ? null : 'netplay_start_cleanup_unconfirmed';
    stage.error = stage.cleanupErrors[0] || null;
    if (!ticket.initializing && ticket.outcome) {
      session.settle({ start: 'superseded', recording: 'off', stages: [stage], cleanupErrors: stage.cleanupErrors });
    }
  }
  for (const result of ticket.evidenceTargets) result.cleanupErrors = uniqueErrors(result.cleanupErrors, ticket.cleanupErrors);
}

/** Move an initialized core's ownership before a prior await can reclaim it. */
export function transferEarlierCore(earlier, resources) {
  if (!earlier.ownerEmu) return;
  const previous = earlier.session?.resources;
  const ticket = previous?.coreTicket?.emu === earlier.ownerEmu
    ? previous.coreTicket : coreRetirementTicket(earlier.ownerEmu);
  earlier.coreTicket = resources.coreTicket = ticket;
  if (earlier.session) {
    ticket.waitingSessions.set(earlier.session, earlier);
    earlier.session.pendingCoreTicket = ticket;
    if (previous?.sessionEmu === earlier.ownerEmu) previous.sessionEmu = null;
    if (previous) previous.transferredCoreTicket = ticket;
  }
}

/** The caller invokes destroy; this retains its original Error and post-call clock. */
export function recordCoreRetirement(ticket, cleanupDeadline, errors = []) {
  if (ticket.outcome) return ticket.outcome;
  ticket.cleanupErrors = uniqueErrors(ticket.cleanupErrors, errors);
  if (ticket.initializing) ticket.cleanupErrors = uniqueErrors(ticket.cleanupErrors,
    [new Error('old core initialization cleanup unconfirmed')]);
  ticket.late ||= monoNow() >= cleanupDeadline;
  ticket.outcome = { cleanupErrors: ticket.cleanupErrors,
    confirmed: ticket.cleanupErrors.length === 0 && !ticket.late && !ticket.initializing, late: ticket.late };
  refreshCoreEvidence(ticket);
  return ticket.outcome;
}

/** A delayed init rejection belongs to the already refused cleanup evidence. */
export function recordCoreInitializationFailure(ticket, error) {
  if (!ticket.outcome) return;
  ticket.cleanupErrors = uniqueErrors(ticket.cleanupErrors, [error]);
  ticket.outcome.cleanupErrors = ticket.cleanupErrors;
  ticket.outcome.confirmed = false;
  refreshCoreEvidence(ticket);
}

/** An early destroy cannot retire resources constructed after the init resumes. */
export function finishCoreInitialization(ticket, resources) {
  ticket.initializing = false;
  if (ticket.outcome) {
    ticket.outcome = null;
    resources.sessionEmu = ticket.emu;
  }
}

/** Share the same independent media fallback for startup and optional voice drop. */
export function retireVoiceResources(voice) {
  const cleanupErrors = [];
  const step = fn => { try { fn(); } catch (error) { cleanupErrors.push(error); } };
  const mic = voice._micTrack, stream = voice._micStream, pc = voice.pc, microphoneOwner = voice._microphoneOwner;
  try { voice.destroy(); } catch (error) {
    cleanupErrors.push(error);
    if (microphoneOwner) step(() => microphoneOwner.close());
    const stopped = new Set();
    const stop = track => { if (track && !stopped.has(track)) { stopped.add(track); step(() => track.stop()); } };
    stop(mic);
    step(() => { for (const track of stream?.getTracks?.() || []) stop(track); });
    if (pc) step(() => pc.close());
  }
  return { cleanupErrors: uniqueErrors(cleanupErrors), confirmed: cleanupErrors.length === 0 };
}

/**
 * §6 Refused startup: retire each resource on its own, the app reference
 * cleared before destroy(). A destroy that throws still stops that object's
 * media tracks and closes its peer connections. This startup still owns the
 * objects in res even after app references change. It destroys those objects
 * without clearing replacement references; running is cleared only with this
 * startup's own core. Runs once and caches the structured outcome.
 */
export function retireLockstepResources(app, res, cleanupDeadline) {
  if (res.retirementOutcome) return res.retirementOutcome;
  const outcome = res.retirementOutcome = { cleanupErrors: [], confirmed: false, late: false };
  if (res.retired) return outcome;
  res.retired = true;
  const errors = outcome.cleanupErrors;
  const step = (fn) => { try { fn(); } catch (error) { errors.push(error); } };
  const stopTracks = (stream) => step(() => {
    for (const track of stream?.getTracks?.() || []) step(() => track.stop());
  });
  const close = (closable) => { if (closable) step(() => closable.close()); };
  step(() => res.restoreHandlers?.());
  const stream = res.soloStream;
  if (stream) {
    if (app._soloStream === stream) app._soloStream = null;
    try { stream.destroy(); } catch (error) {
      errors.push(error);
      step(() => { for (const peer of stream._peers?.values?.() || []) close(peer); });
      step(() => stopTracks(stream._sourceStream));
    }
  }
  const voice = res.voiceChat;
  if (voice) {
    if (app._voiceChat === voice) app._voiceChat = null;
    errors.push(...retireVoiceResources(voice).cleanupErrors);
  }
  const engine = res.engine;
  if (engine) {
    if (app.lockstep === engine) app.lockstep = null;
    step(() => engine.destroy());
  }
  const conn = res.conn;
  if (conn) {
    if (app.lockstepConn === conn) app.lockstepConn = null;
    try { conn.destroy(); } catch (error) {
      errors.push(error);
      step(() => close(conn.inputChannel));
      step(() => close(conn.probeChannel));
      step(() => close(conn.pc));
    }
  }
  if (res.autoSaveInterval) {
    step(() => clearInterval(res.autoSaveInterval));
    if (app._autoSaveInterval === res.autoSaveInterval) app._autoSaveInterval = null;
  }
  const emu = res.sessionEmu;
  const recorder = res.recorder;
  if (recorder) {
    const bytes = recorderRetainedBytes(recorder);
    if (app.replayRecorder === recorder) app.replayRecorder = null;
    step(() => recorder.abortOperations?.());
    let stopFailed = false;
    try { recorder.stop(); } catch (error) { stopFailed = true; errors.push(error); }
    step(() => drainRecorder(app, recorder, { stopFailed, bytes }));
  }
  if (emu) {
    if (app.emu === emu) {
      app.running = false;
      step(() => app.input?.stop?.());
      app.emu = null;
    }
    const ticket = res.coreTicket?.emu === emu ? res.coreTicket : coreRetirementTicket(emu);
    res.coreTicket = ticket;
    if (!ticket.outcome) {
      const coreErrors = [];
      if (res.captureUnsettled) coreErrors.push(new Error('old state capture cleanup unconfirmed'));
      else { try { emu.destroy?.(); } catch (error) { coreErrors.push(error); } }
      recordCoreRetirement(ticket, cleanupDeadline, coreErrors);
    }
    errors.push(...ticket.outcome.cleanupErrors);

  }
  // Capture ownership remains active even when a legacy core slot is empty.
  if (res.captureUnsettled && !emu) errors.push(new Error('old state capture cleanup unconfirmed'));
  const late = monoNow() >= cleanupDeadline;
  Object.assign(outcome, { confirmed: errors.length === 0 && !late, late });
  return outcome;
}

/** Capture old and new resources separately before any startup await. */
export function captureEarlierSession(app) {
  return { ownerEmu: app.emu || null, recorder: app.replayRecorder || null,
    session: app._lockstepSession || null,
    animFrameId: app.animFrameId, lockstepTimer: app._lockstepTimer,
    net: app.net, resetHandler: app.net?.onLockstepReset,
    nativeResources: { restoreHandlers: app._lockstepSession?.resources?.restoreHandlers,
      soloStream: app._soloStream || null, voiceChat: app._voiceChat || null,
      engine: app.lockstep || null, conn: app.lockstepConn || null,
      autoSaveInterval: app._autoSaveInterval || null } };
}

/**
 * §6 Earlier session: stop the old loop, retire its engine and connection with
 * the steps of _stopLockstep() (each on its own), then detach and stop its
 * recorder without aborting it, so frozen captures may finish.
 */
export function retireEarlierSession(app, cleanupDeadline = monoNow() + (CLEANUP_MS - STAGE_MS), earlier = captureEarlierSession(app)) {
  const errors = [];
  let resourceConfirmed = true;
  let resourceLate = false;
  const step = (fn) => { try { fn(); } catch (error) { errors.push(error); } };
  const current = (app.emu || null) === earlier.ownerEmu
    && (!earlier.startupSession || app._lockstepSession === earlier.startupSession);
  if (current) app.running = false;
  if (earlier.animFrameId) step(() => cancelAnimationFrame(earlier.animFrameId));
  if (app.animFrameId === earlier.animFrameId) app.animFrameId = null;
  if (earlier.lockstepTimer) step(() => clearTimeout(earlier.lockstepTimer));
  if (app._lockstepTimer === earlier.lockstepTimer) app._lockstepTimer = null;
  if (current) {
    step(() => { if (earlier.resetHandler != null && earlier.net?.onLockstepReset === earlier.resetHandler) earlier.net.onLockstepReset = null; });
    step(() => app._stopMicLevelLoop?.());
    step(() => document.getElementById('btn-sidebar-mic')?.classList.remove('mic-active'));
  }
  // Each old stream/voice/engine/connection/interval is independent, including
  // voice or spectator-only remnants after an engine has already disappeared.
  const retired = retireLockstepResources(app, earlier.nativeResources, cleanupDeadline);
  resourceConfirmed = retired.confirmed;
  resourceLate = retired.late;
  errors.push(...retired.cleanupErrors);
  if (current) step(() => app._restoreHUDLabels?.());
  const recorder = earlier.recorder;
  if (recorder && !earlier.recorderRetired) {
    earlier.recorderRetired = true;
    if (app.replayRecorder === recorder) app.replayRecorder = null;
    recorder.detachedSource = true;
    const bytes = recorderRetainedBytes(recorder);
    let stopFailed = false;
    try { recorder.stop(); } catch (error) { stopFailed = true; errors.push(error); }
    step(() => drainRecorder(app, recorder, { stopFailed, bytes }));
  }
  const late = resourceLate || monoNow() >= cleanupDeadline;
  const confirmed = resourceConfirmed && errors.length === 0 && !late;
  const result = { stage: 'earlier_retirement', status: confirmed ? 'ok' : late ? 'timeout' : 'failed',
    cause: confirmed ? null : 'netplay_start_cleanup_unconfirmed', error: errors[0] || null,
    cleanupErrors: errors, confirmed, late };
  earlier.retirementResult = result;
  earlier.nativeRetirement = { cleanupErrors: errors, confirmed, late };
  if (earlier.coreTicket?.outcome) refreshCoreEvidence(earlier.coreTicket);
  else if (!earlier.coreTicket) earlier.session?.settle({ start: 'superseded', recording: 'off', stages: [result], cleanupErrors: errors });

  const previous = earlier.session?.resources;
  if (previous) {
    for (const key of ['soloStream', 'voiceChat', 'engine', 'conn', 'autoSaveInterval']) {
      if (previous[key] === earlier.nativeResources[key]) previous[key] = null;
    }
  }
  return result;
}

/** §6 Per-launch reset, as _startSolo() does. Never clears a quarantine. */
export function resetLockstepLaunchState(app) {
  app._replayBaseStateLoaded = false;
  app._replayRecordingOff = false;
  app._clearPendingBaseGeneration?.();
  app._recordResumePrefix = null;
  app.soloFrame = 0;
  app._libretroSoloFrame = 0;
  app._recordFrameOffset = 0;
  app._recordDrainCursor = 0;
  app._replayBoundaryOwner = null;
  // Active capture flags/queues belong to their capture, not this reset.
  app._replaySessionGen = (app._replaySessionGen || 0) + 1;
  app._lockstepStartReleased = false;
  app._lockstepAppliedFrame = false;
  app._lockstepAppliedSinceReset = 0;
  app._lockstepExpectedFrame = null;
  app._remoteResetPending = null;
  app._netplayResetTimes = [];
  app._netplayResetEpoch = 0;
  app._netplayResetDelivery = null;
}

const MAX_FRAME = 0x7fffffff;
const JSNES_FRAME_BUFFERS = ['buffer', 'bgbuffer', 'pixrendered'];

/** §6: absent or null means 0; anything else must be an integer 0..2^31-1. */
export function validateSaveFrame(frame) {
  if (frame === undefined || frame === null) return { ok: true, frame: 0 };
  return Number.isInteger(frame) && frame >= 0 && frame <= MAX_FRAME ? { ok: true, frame } : { ok: false, frame: null };
}

/** §6 Fresh core: a ROM load is complete only with a loaded game. */
export function romLoadComplete(emu) {
  const result = (status, error = null) => ({ stage: 'rom_load', status,
    cause: status === 'ok' ? null : 'netplay_start_core_unavailable', error });
  try {
    if (emu?.loaded !== true) return result('failed', new Error('ROM not loaded'));
    if (emu.coreName === 'jsnes') return emu.nes?.mmap
      ? result('ok') : result('failed', new Error('jsnes mapper missing'));
    const m = emu.module;
    if (typeof m?._lockstep_step_frame !== 'function' || typeof m._retroplay_serialize_size !== 'function')
      return result('unsupported', new Error('ROM completion exports missing'));
    const size = m._retroplay_serialize_size();
    return Number.isSafeInteger(size) && size > 0
      ? result('ok') : result('failed', new RangeError('invalid loaded serialize size'));
  } catch (error) { return result('failed', error); }
}

function isPlainObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value) || ArrayBuffer.isView(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function hasUndefined(value, seen = new Set()) {
  if (value === undefined) return true;
  if (!value || typeof value !== 'object' || ArrayBuffer.isView(value) || seen.has(value)) return false;
  seen.add(value);
  return Object.values(value).some((child) => hasUndefined(child, seen));
}

/** jsnes state as JSON without the PPU frame buffers; Tile.toJSON() normalizes
 *  ppu.scantile the way normalizeJsnesState() does. */
function canonicalJsnes(state) {
  const ppu = isPlainObject(state.ppu)
    ? Object.fromEntries(Object.entries(state.ppu).filter(([key]) => !JSNES_FRAME_BUFFERS.includes(key)))
    : state.ppu;
  return JSON.stringify({ ...state, ppu });
}

/** §6: a complete synchronous load, or a refusal with the error kept. */
export function confirmLockstepLoad(emu, state) {
  try {
    if (emu.coreName === 'jsnes') {
      if (!isPlainObject(state)) return { ok: false, error: new TypeError('jsnes state is not a plain object') };
      const expected = canonicalJsnes(state);         // before loadState() adds the frame buffers
      emu.loadState(state);
      const again = emu.nes?.toJSON?.();
      if (!again) return { ok: false, error: new Error('jsnes state cannot be re-serialized') };
      // A key the state lacks reloads as undefined, which JSON would drop.
      if (hasUndefined(again) || canonicalJsnes(again) !== expected) {
        return { ok: false, error: new Error('jsnes state did not round-trip') };
      }
      return { ok: true, error: null };
    }
    if (!(state instanceof Uint8Array) || state.length === 0) return { ok: false, error: new TypeError('state is not bytes') };
    if (typeof emu.module?._retroplay_unserialize !== 'function') {
      return { ok: false, error: new Error('no synchronous unserialize export') };
    }
    // The pinned loadState() selects its synchronous branch solely from these
    // two conditions. Inside that branch it validates size (including its
    // supported legacy/variable layouts) and returns false for an invalid size;
    // it never falls through to the file command after a size refusal.
    return emu.loadState(state) === true ? { ok: true, error: null } : { ok: false, error: new Error('unserialize refused the state') };
  } catch (error) {
    return { ok: false, error };
  }
}

/** §6 origin check: count every reset, reload, load and step of the session core. */
export function countCoreMutations(emu) {
  let n = 0;
  for (const name of ['reset', 'loadROM', 'loadState', 'stepFrame']) {
    const original = emu?.[name];
    if (typeof original !== 'function') continue;
    emu[name] = function counted(...args) { n += 1; return original.apply(this, args); };
  }
  return { count: () => n };
}
